import React from 'react';
import { Box, Stack, Tooltip, useTheme } from '@mui/material';
import type { TooltipProps } from '@mui/material';

import { CategoryCell } from '../Table/components/Cells/Cells';

interface MoreCategoriesTooltipProps {
  /** The HIDDEN categories to list in the overflow popover. */
  categories: string[];
  filter?: boolean;
  placement?: TooltipProps['placement'];
  /**
   * Which taxonomy the strings belong to (VRG-628). Forwarded to `CategoryCell`;
   * defaults to `'category'`. The overflow rows must resolve their glyphs the same
   * way the visible ones do, or a list reads as two different icon sets split at
   * `maxVisible`.
   */
  kind?: 'category' | 'productType';
  /** The "+N more" trigger element. */
  children: React.ReactElement;
}

/**
 * The shared "+N more" category-overflow popover (VRG-156). A single source for
 * how the hidden-category list renders — theme-aware surface, roomy ~44px rows
 * matching the app's menus — so every "Best for +N more" (the tag-shop list card
 * AND the product page's CategoryList) looks identical rather than each hand-rolling
 * its own tighter/looser tooltip.
 */
export const MoreCategoriesTooltip: React.FC<MoreCategoriesTooltipProps> = ({
  categories,
  filter = false,
  kind = 'category',
  // Open to the RIGHT of the "+N more" trigger so a long category list doesn't
  // run off the bottom of the page (MUI flips it leftward if there's no room).
  placement = 'right-start',
  children,
}) => {
  const theme = useTheme();
  return (
    <Tooltip
      placement={placement}
      slotProps={{
        tooltip: {
          sx: {
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            border: `1px solid ${theme.palette.primary[100]}`,
            borderRadius: 2,
            boxShadow: theme.shadows[3],
            p: 1.5,
            maxWidth: 340,
          },
        },
      }}
      title={
        <Stack spacing={0.5}>
          {categories.map((category, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                alignItems: 'center',
                minHeight: 44,
                px: 2,
                borderRadius: 1.5,
                '&:hover': { backgroundColor: theme.palette.action.hover },
              }}
            >
              <CategoryCell category={category} filter={filter} kind={kind} />
            </Box>
          ))}
        </Stack>
      }
    >
      {children}
    </Tooltip>
  );
};
